import joborderService from '../../../services/joborderService';
import filterService from '../../../services/filterService';


import {
    setAllJobOrderLoading,
    setAllJobOrderError,
    setAllJobOrderList,
    setAllJobOrderCurrent,
    setAllJobOrderPagination,
    setAllJobOrderSort,
    setAllJobOrderFilters,
    setAllJobOrderDefaultFilter,
    setAllJobOrderSettings,
    clearAllJobOrderCurrent,
    clearAllJobOrderError,
    setAllJobOrderFilterLoading,
    changeAllJobOrderFilterCondition,
    addAllJobOrderFilterCondition,
    updateAllJobOrderFilterConditionRule,
    updateAllJobOrderFilterConditionValue,
    removeAllJobOrderFilterCondition,
    setAllJobOrderSelected,
    resetAllJobOrderFiltersAndPagination
} from './alljoborderSlice';

export const fetchSettings = () => async (dispatch, getState) => {
    try {
        // Only fetch settings if they haven't been initialized
        if (!getState().alljoborder.isSettingsInitialized) {
            dispatch(setAllJobOrderLoading(true));
            const response = await joborderService.getSettings();

            // Set the settings
            dispatch(setAllJobOrderSettings(response));

            const activeFilter = response.filters.find(filter => filter.mark_default === true)
            if (activeFilter) {
                dispatch(setAllJobOrderDefaultFilter(activeFilter));
                return activeFilter;
            }
        }
        return {};
    } catch (error) {
        console.error('Error fetching settings:', error);
        dispatch(setAllJobOrderError({
            message: error.message || 'Failed to fetch settings',
            errors: error.errors || {}
        }));
        return {};
    } finally {
        dispatch(setAllJobOrderLoading(false));
    }
};

export const fetchAlljoborders = (params = {}, filter = {}, shouldUseFilter, externalCondition = {}) => async (dispatch, getState) => {
    try {
        // Check if settings are initialized

        const state = getState().alljoborder;
        if (!state.isSettingsInitialized) {
            shouldUseFilter ? filter = await dispatch(fetchSettings()) : await dispatch(fetchSettings());
        }

        const finalQueryParams = {
            ...state.filters,
            page: state.pagination.current_page,
            per_page: state.pagination.per_page,
            sort_field: state.sort.field,
            sort_direction: state.sort.direction,
            ...params
        };

        const finalActiveFilter = shouldUseFilter ? (Object.keys(filter).length === 0 ? state.defaultFilter : { ...state.defaultFilter, ...filter }) : externalCondition;

        dispatch(setAllJobOrderLoading(true));
        const response = await joborderService.getAllList(finalQueryParams, finalActiveFilter);
        dispatch(setAllJobOrderList(response));
        return response;
    } catch (error) {
        console.error('Error in fetchAlljoborders:', error);
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {},
            status: error.status || 500
        };
        dispatch(setAllJobOrderError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setAllJobOrderLoading(false));
    }
};

export const fetchAllJobOrder = (id) => async (dispatch) => {
    try {
        dispatch(setAllJobOrderLoading(true));
        const response = await joborderService.getOne(id);
        dispatch(setAllJobOrderCurrent(response.data));
    } catch (error) {
        dispatch(setAllJobOrderError({
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        }));
    } finally {
        dispatch(setAllJobOrderLoading(false));
    }
};

export const createAllJobOrder = (data) => async (dispatch) => {
    try {
        const response = await joborderService.create(data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const updateAllJobOrder = (id, data) => async (dispatch) => {
    try {
        const response = await joborderService.update(id, data);
        return response.data || response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        throw serializedError;
    }
};

export const deleteAllJobOrder = (id) => async (dispatch) => {
    try {
        dispatch(setAllJobOrderLoading(true));
        const response = await joborderService.delete(id);
        return response;
    } catch (error) {
        const serializedError = {
            message: error.message || 'An error occurred',
            errors: error.errors || {}
        };
        dispatch(setAllJobOrderError(serializedError));
        throw serializedError;
    } finally {
        dispatch(setAllJobOrderLoading(false));
    }
};

export const setPagination = (pagination) => (dispatch) => {
    dispatch(setAllJobOrderPagination(pagination));
};

export const setSort = (sort) => (dispatch) => {
    dispatch(setAllJobOrderSort(sort));
};

export const searchAlljoborders = (query) => (dispatch, getState) => {
    const currentFilters = getState().alljoborder.filters;
    dispatch(setAllJobOrderFilters({ ...currentFilters, search: query }));
};

// New filter-related actions
export const setDefaultFilter = (data) => {
    return async (dispatch) => {
        try {
            await dispatch(setAllJobOrderDefaultFilter(data));
        } catch (error) {
            console.error('Error setting default filter:', error);
        }
    };
};

export const changeCondition = (condition) => {
    return (dispatch) => {
        dispatch(changeAllJobOrderFilterCondition(condition));
    };
};

export const addNewCondition = (rule) => {
    return (dispatch) => {
        dispatch(addAllJobOrderFilterCondition(rule));
    };
};

export const updateOptionRule = (index, data) => {
    return (dispatch) => {
        dispatch(updateAllJobOrderFilterConditionRule({ index, data }));
    };
};

export const updateOptionValue = (index, value) => {
    return (dispatch) => {
        dispatch(updateAllJobOrderFilterConditionValue({ index, value }));
    };
};

export const removeCondition = (index) => {
    return (dispatch) => {
        dispatch(removeAllJobOrderFilterCondition(index));
    };
};

export const saveFilterCondition = (filterData) => async (dispatch, getState) => {
    try {
        dispatch(setAllJobOrderFilterLoading(true));
        const response = await filterService.createFilterCondition(filterData);
        if (!response?.errors) {
            const currentSettings = getState().alljoborder.settings;
            dispatch(setAllJobOrderSettings({ ...currentSettings, filters: response.filters }));
        }
        dispatch(setAllJobOrderFilterLoading(false));
        return response;
    } catch (error) {
        console.error('Error saving filter:', error);
        dispatch(setAllJobOrderFilterLoading(false));
        dispatch(setAllJobOrderError({
            message: error.message || 'Failed to save filter',
            errors: error.errors || {}
        }));
        throw error;
    }
};

export const setSelected = (selectedRows) => {
    return (dispatch) => {
        dispatch(setAllJobOrderSelected(selectedRows));
    };
};

export const clearCurrentAllJobOrder = () => {
    return (dispatch) => {
        dispatch(clearAllJobOrderCurrent());
    };
};

export const resetFiltersAndPagination = () => (dispatch) => {
    dispatch(resetAllJobOrderFiltersAndPagination());
};